import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LiveCounter } from "@/components/LiveCounter";
import { fadeUp, viewportOnce } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistFooter = ({ count, onJoined }) => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { phone, city: "Bhubaneswar" });
      setDone(true);
      if (onJoined) onJoined();
      toast.success("You're on the VIP list! Free delivery secured 🎉");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="waitlist"
      className="relative overflow-hidden"
      style={{ backgroundColor: "var(--trust-blue)" }}
      data-testid="waitlist-footer"
    >
      <div className="noise-overlay" />
      {/* glow accents */}
      <div
        className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full blur-3xl"
        style={{ backgroundColor: "rgba(30,132,73,0.25)" }}
      />
      <div
        className="pointer-events-none absolute -right-16 bottom-0 h-64 w-64 rounded-full blur-3xl"
        style={{ backgroundColor: "rgba(234,88,12,0.18)" }}
      />

      <div className="relative z-10 mx-auto max-w-3xl px-5 py-24 text-center sm:px-8 sm:py-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <span
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300"
            data-testid="launch-badge"
          >
            📍 Phase 1 · Bhubaneswar
          </span>
        </motion.div>

        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="font-display mt-6 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl"
        >
          Be the first to know when we go live.
        </motion.h2>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="mx-auto mt-5 max-w-xl text-base text-slate-300 sm:text-lg"
        >
          Drop your phone number to get{" "}
          <span className="font-semibold text-white">FREE delivery</span> on your
          first order.
        </motion.p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
          className="mt-8 flex justify-center"
        >
          <LiveCounter count={count} dark testId="footer-live-counter" />
        </motion.div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-10 max-w-md rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-8"
            data-testid="waitlist-success"
          >
            <PartyPopper
              className="mx-auto mb-3 text-emerald-400"
              size={34}
            />
            <p className="font-display text-xl font-bold text-white">
              You&apos;re on the VIP list!
            </p>
            <p className="mt-2 text-sm text-slate-300">
              We&apos;ll text you the moment Medicines Now goes live in
              Bhubaneswar. Your free first delivery is locked in.
            </p>
          </motion.div>
        ) : (
          <motion.form
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
            onSubmit={handleSubmit}
            className="mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row"
            data-testid="waitlist-form"
          >
            <div className="flex flex-1 items-center rounded-full border border-white/15 bg-white/5 px-5 backdrop-blur">
              <span className="mr-2 text-sm font-medium text-slate-400">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                aria-label="Phone Number"
                data-testid="waitlist-phone-input"
                className="w-full bg-transparent py-4 text-white placeholder:text-slate-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="waitlist-submit-button"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 py-4 text-base font-semibold text-white transition-[transform,box-shadow] duration-300 hover:scale-[1.03] glow-green disabled:opacity-70"
              style={{ backgroundColor: "var(--action-green)" }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Joining…
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} /> Claim Free Delivery
                </>
              )}
            </button>
          </motion.form>
        )}

        <p className="mt-4 text-xs text-slate-400">
          No spam. Just one text when we launch.
        </p>

        {/* Footer bar */}
        <div className="mt-20 border-t border-white/10 pt-8">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            <Logo dark size="sm" testId="footer-logo" />
            <nav className="flex items-center gap-6 text-sm text-slate-400">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-white"
              >
                <Instagram size={16} /> Instagram
              </a>
              <a
                href="/privacy"
                data-testid="footer-privacy-link"
                className="transition-colors duration-200 hover:text-white"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                data-testid="footer-terms-link"
                className="transition-colors duration-200 hover:text-white"
              >
                Terms of Service
              </a>
            </nav>
          </div>
          <p className="mt-8 text-xs text-slate-500">
            © {new Date().getFullYear()} Medicines Now · India&apos;s Medicine
            Search Engine.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default WaitlistFooter;
